import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Clock, Briefcase, DollarSign, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '../../../lib/utils';
import { Job } from '../../../types/jobs';
import { formatSalaryRange, formatExperience } from '../../../lib/careers';

interface JobListingsProps {
  jobs: Job[];
  loading: boolean;
  error: string | null;
}

const JobListings = ({ jobs, loading, error }: JobListingsProps) => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const categories = useMemo(() => {
    return [...new Set(jobs.map(job => job.category))].sort();
  }, [jobs]);

  const filteredJobs = useMemo(() => {
    if (!selectedCategory) return jobs;
    return jobs.filter(job => job.category === selectedCategory);
  }, [jobs, selectedCategory]);

  if (loading) {
    return (
      <section className="py-24 bg-black">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
            <span className="ml-4 text-white">Loading jobs...</span>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-24 bg-black">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <h2 className="text-2xl font-display font-semibold text-white mb-4">
              Unable to Load Jobs
            </h2>
            <p className="text-gray-400">{error}</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-24 bg-black">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-display font-semibold text-white mb-6">
            Open Positions
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Discover exciting opportunities to grow your career with us
          </p>
        </motion.div>

        {/* Department Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex flex-wrap justify-center gap-4 mb-12"
        >
          <button
            onClick={() => setSelectedCategory(null)}
            className={cn(
              'px-4 py-2 border rounded-full text-sm transition-colors duration-300',
              selectedCategory === null
                ? 'bg-accent text-white border-accent'
                : 'bg-white/5 border-white/10 text-gray-300 hover:border-accent/50'
            )}
          >
            All Categories
          </button>

          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={cn(
                'px-4 py-2 border rounded-full text-sm transition-colors duration-300',
                selectedCategory === category
                  ? 'bg-accent text-white border-accent'
                  : 'bg-white/5 border-white/10 text-gray-300 hover:border-accent/50'
              )}
            >
              {category}
            </button>
          ))}
        </motion.div>

        {/* Job Cards */}
        <div className="grid gap-6 max-w-4xl mx-auto">
          {filteredJobs.map((job, index) => (
            <motion.div
              key={job.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <Link
                to={`/careers/${job.slug}`}
                className={cn(
                  'group block p-8 rounded-xl bg-white/5 border border-white/10',
                  'hover:bg-white/10 hover:border-accent/50 transition-all duration-300'
                )}
              >
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="px-3 py-1 bg-accent/10 text-accent rounded-full text-sm font-medium">
                        {job.category}
                      </span>

                      <span className="text-gray-400 text-sm">
                        Posted {new Date(job.created_at).toLocaleDateString()}
                      </span>
                    </div>

                    <h3 className="text-2xl font-display font-semibold text-white mb-3 group-hover:text-accent transition-colors duration-300">
                      {job.role}
                    </h3>

                    <p className="text-gray-400 mb-4 line-clamp-2">
                      {job.about_role}
                    </p>

                    <div className="flex flex-wrap gap-4 text-sm text-gray-400">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        <span>{job.location}</span>
                      </div>

                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        <span>
                          {job.type} • {job.workplace}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <Briefcase className="w-4 h-4" />
                        <span>{formatExperience(job.experience)}</span>
                      </div>

                      {formatSalaryRange(job.salary_min, job.salary_max) && (
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-4 h-4" />
                          <span>
                            {formatSalaryRange(job.salary_min, job.salary_max)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-accent group-hover:translate-x-1 transition-all duration-300" />
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default JobListings;